/** Ticket to Talk — multi-class data shapes (2026-09-16) */

export type ChoiceLetter = "A" | "B" | "C" | "D";

export type LicenseClass = "Technician" | "General" | "Extra";

export type ClassKey = "technician" | "general" | "extra";

export type Question = {
  id: string;
  subelement: string;
  group: string;
  fccRefs?: string[];
  text: string;
  choices: { A: string; B: string; C: string; D: string };
  answer: ChoiceLetter;
  diagram: string | null;
  tags: string[];
  explanation: string;
  hint: string;
  vignetteId?: string;
  withdrawn?: boolean;
};

export type PoolFile = {
  element: 2 | 3 | 4;
  licenseClass: LicenseClass;
  poolVersion: string;
  revision: string;
  effectiveFrom: string;
  effectiveTo: string;
  source: string;
  attribution: string;
  questionCount: number;
  diagrams: string[];
  questions: Question[];
};

export type DrillFilter =
  | { kind: "subelement"; id: string }
  | { kind: "group"; id: string }
  | { kind: "tag"; tag: string };

export type AttemptDraftItem = {
  questionId: string;
  order: number;
  choiceMap: ChoiceLetter[];
};

export type AttemptDraft = {
  mode: "full_mock" | "section_drill";
  poolVersion: string;
  revision: string;
  rngSeed: string;
  items: AttemptDraftItem[];
  hintBudget: number;
};

export type AttemptResultItem = {
  questionId: string;
  order: number;
  choiceMap: ChoiceLetter[];
  selected: ChoiceLetter | null;
  correct: ChoiceLetter;
  isCorrect: boolean;
  hintUsed: boolean;
};

export type AttemptResult = {
  mode: "full_mock" | "section_drill";
  poolVersion: string;
  revision: string;
  /** Element number for namespacing across classes */
  element: 2 | 3 | 4;
  licenseClass: LicenseClass;
  rngSeed: string;
  startedAt: string;
  finishedAt: string;
  score: number;
  total: number;
  passed: boolean | null;
  hintUsed: boolean;
  items: AttemptResultItem[];
};

export type LocalProgress = {
  profile: {
    tz: string;
    createdAt: string;
    activePoolVersion: string;
    preferredClass?: ClassKey;
  };
  attempts: AttemptResult[];
};

/** Official Element 2 group list for a 35-question mock. */
export const TECHNICIAN_GROUPS: readonly string[] = [
  "T1A", "T1B", "T1C", "T1D", "T1E", "T1F",
  "T2A", "T2B", "T2C",
  "T3A", "T3B", "T3C",
  "T4A", "T4B",
  "T5A", "T5B", "T5C", "T5D",
  "T6A", "T6B", "T6C", "T6D",
  "T7A", "T7B", "T7C", "T7D",
  "T8A", "T8B", "T8C", "T8D",
  "T9A", "T9B",
  "T0A", "T0B", "T0C",
] as const;

/** Official Element 3 group list for a 35-question mock. */
export const GENERAL_GROUPS: readonly string[] = [
  "G1A", "G1B", "G1C", "G1D", "G1E",
  "G2A", "G2B", "G2C", "G2D", "G2E",
  "G3A", "G3B", "G3C",
  "G4A", "G4B", "G4C", "G4D", "G4E",
  "G5A", "G5B", "G5C",
  "G6A", "G6B",
  "G7A", "G7B", "G7C",
  "G8A", "G8B", "G8C",
  "G9A", "G9B", "G9C", "G9D",
  "G0A", "G0B",
] as const;

/** Official Element 4 group list for a 50-question mock. */
export const EXTRA_GROUPS: readonly string[] = [
  "E1A", "E1B", "E1C", "E1D", "E1E", "E1F",
  "E2A", "E2B", "E2C", "E2D", "E2E",
  "E3A", "E3B", "E3C",
  "E4A", "E4B", "E4C", "E4D", "E4E",
  "E5A", "E5B", "E5C", "E5D",
  "E6A", "E6B", "E6C", "E6D", "E6E", "E6F",
  "E7A", "E7B", "E7C", "E7D", "E7E", "E7F", "E7G", "E7H",
  "E8A", "E8B", "E8C", "E8D",
  "E9A", "E9B", "E9C", "E9D", "E9E", "E9F", "E9G", "E9H",
  "E0A",
] as const;

export type ClassConfig = {
  key: ClassKey;
  licenseClass: LicenseClass;
  element: 2 | 3 | 4;
  poolPath: string;
  groups: readonly string[];
  mockTotal: number;
  passScore: number;
  elementLabel: string;
};

export const CLASS_CONFIGS: Record<ClassKey, ClassConfig> = {
  technician: {
    key: "technician",
    licenseClass: "Technician",
    element: 2,
    poolPath: "./pools/technician/2026-2030.json",
    groups: TECHNICIAN_GROUPS,
    mockTotal: 35,
    passScore: 26,
    elementLabel: "Element 2",
  },
  general: {
    key: "general",
    licenseClass: "General",
    element: 3,
    poolPath: "./pools/general/2023-2027.json",
    groups: GENERAL_GROUPS,
    mockTotal: 35,
    passScore: 26,
    elementLabel: "Element 3",
  },
  extra: {
    key: "extra",
    licenseClass: "Extra",
    element: 4,
    poolPath: "./pools/extra/2024-2028.json",
    groups: EXTRA_GROUPS,
    mockTotal: 50,
    passScore: 37,
    elementLabel: "Element 4",
  },
};

/** @deprecated Use CLASS_CONFIGS.technician.passScore */
export const MOCK_PASS_SCORE = 26;
/** @deprecated Use CLASS_CONFIGS.technician.mockTotal */
export const MOCK_TOTAL = 35;
export const DRILL_TOTAL = 10;

export const CLASS_STORAGE_KEY = "gota-ttt-class-v1";

export function parseClassKey(raw: string | null | undefined): ClassKey | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v === "technician" || v === "tech" || v === "t") return "technician";
  if (v === "general" || v === "gen" || v === "g") return "general";
  if (v === "extra" || v === "e") return "extra";
  return null;
}

export function configForPool(pool: PoolFile): ClassConfig {
  if (pool.element === 3 || pool.licenseClass === "General") return CLASS_CONFIGS.general;
  if (pool.element === 4 || pool.licenseClass === "Extra") return CLASS_CONFIGS.extra;
  return CLASS_CONFIGS.technician;
}

export function groupsForPool(pool: PoolFile): readonly string[] {
  return configForPool(pool).groups;
}

export function passScoreForPool(pool: PoolFile): number {
  return configForPool(pool).passScore;
}

export function mockTotalForPool(pool: PoolFile): number {
  return configForPool(pool).mockTotal;
}
